import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bot, Settings, Shield, Check, X, AlertTriangle, 
  Sparkles, Package, Mail, Calculator, ChefHat, 
  FileSpreadsheet, Users, Target, ChevronRight,
  ToggleLeft, ToggleRight, Info, Clock, Activity,
  Lock, Unlock, Eye, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Icon-Mapping
const MODULE_ICONS = {
  ki_warenwirtschaft: Package,
  ki_marketing: Mail,
  ki_kalkulation: Calculator,
  ki_kuechenleiter: ChefHat,
  ki_buchhaltung: FileSpreadsheet,
  ki_personal: Users,
  ki_gastemanager: Target
};

export default function KIControlCenter() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [modules, setModules] = useState([]);
  const [modulesByCategory, setModulesByCategory] = useState({});
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [allActions, setAllActions] = useState([]);
  
  // Module Detail Modal
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [modulePermissions, setModulePermissions] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchModules = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ki-control/modules`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setModules(data.modules || []);
        setModulesByCategory(data.by_category || {});
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Modules fetch error:', error);
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ki-control/dashboard`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    }
  }, []);

  const fetchPendingActions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ki-control/actions/pending`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPendingActions(data.pending_actions || []);
      }
    } catch (error) {
      console.error('Pending actions fetch error:', error);
    }
  }, []);

  const fetchAllActions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ki-control/actions?limit=20`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAllActions(data.actions || []);
      }
    } catch (error) {
      console.error('Actions fetch error:', error);
    }
  }, []);

  const updateModule = async (moduleId, updates) => {
    try {
      const params = new URLSearchParams();
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, typeof value === 'object' ? JSON.stringify(value) : value.toString());
        }
      });
      
      const response = await fetch(`${API_BASE}/api/ki-control/modules/${moduleId}?${params}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('KI-Modul aktualisiert');
        await fetchModules();
        await fetchDashboard();
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const confirmAction = async (actionId, approved) => {
    try {
      const params = new URLSearchParams({ approved: approved.toString() });
      const response = await fetch(`${API_BASE}/api/ki-control/actions/${actionId}/confirm?${params}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success(approved ? 'Aktion genehmigt' : 'Aktion abgelehnt');
        await fetchPendingActions();
        await fetchAllActions();
        await fetchDashboard();
      }
    } catch (error) {
      toast.error('Fehler beim Bestätigen');
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchModules(),
        fetchDashboard(),
        fetchPendingActions(),
        fetchAllActions()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchModules, fetchDashboard, fetchPendingActions, fetchAllActions]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const ModuleIcon = ({ moduleId, className }) => {
    const Icon = MODULE_ICONS[moduleId] || Bot;
    return <Icon className={className} />;
  };

  const openModuleSettings = (module) => {
    setSelectedModule(module);
    const perms = {};
    module.permissions?.forEach((p, key) => {
      if (typeof p === 'object') {
        perms[p.key] = module.permissions[p.key] || p.default;
      } else {
        perms[key] = p;
      }
    });
    setModulePermissions(module.permissions || {});
    setShowModuleModal(true);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen" data-testid="ki-control-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-500" />
          <p className="text-gray-400">Lade KI-Kontrollzentrale...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ki-control-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Bot className="w-8 h-8 text-purple-400" />
          KI-Kontrollzentrale
        </h1>
        <p className="text-gray-400 mt-1">
          Sie entscheiden, welche KI-Module aktiv sind. Die KI macht Vorschläge – Sie entscheiden.
        </p>
      </div>

      {/* Principle Banner */}
      <Card className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-purple-500/30 mb-8">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-purple-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">{dashboard?.principle?.headline || 'KI macht Vorschläge – Sie entscheiden'}</p>
              <p className="text-sm text-gray-300 mt-1">
                {dashboard?.principle?.description || 'Cash-X KI analysiert, warnt und empfiehlt. Kritische Entscheidungen erfordern immer Ihre Bestätigung.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Aktive Module</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {dashboard.overview.active_modules}/{dashboard.overview.total_modules}
                  </p>
                </div>
                <Bot className="w-8 h-8 text-purple-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`border-slate-700 ${dashboard.overview.pending_confirmations > 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/50'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Ausstehende Bestätigungen</p>
                  <p className={`text-2xl font-bold ${dashboard.overview.pending_confirmations > 0 ? 'text-amber-400' : 'text-white'}`}>
                    {dashboard.overview.pending_confirmations}
                  </p>
                </div>
                <AlertTriangle className={`w-8 h-8 ${dashboard.overview.pending_confirmations > 0 ? 'text-amber-400' : 'text-gray-600'} opacity-50`} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Aktionen heute</p>
                  <p className="text-2xl font-bold text-white">{dashboard.overview.today_actions}</p>
                </div>
                <Activity className="w-8 h-8 text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Aktive Rabatte</p>
                  <p className="text-2xl font-bold text-green-400">{dashboard.overview.active_discounts}</p>
                </div>
                <Sparkles className="w-8 h-8 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-purple-600">
            <Bot className="w-4 h-4 mr-2" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="modules" className="data-[state=active]:bg-purple-600">
            <Settings className="w-4 h-4 mr-2" />
            Module
          </TabsTrigger>
          <TabsTrigger value="actions" className="data-[state=active]:bg-purple-600">
            <Clock className="w-4 h-4 mr-2" />
            Aktionen
            {pendingActions.length > 0 && (
              <Badge className="ml-2 bg-amber-500">{pendingActions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="transparency" className="data-[state=active]:bg-purple-600">
            <Eye className="w-4 h-4 mr-2" />
            Transparenz
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Recommendations */}
          {dashboard?.recommendations && dashboard.recommendations.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  KI-Empfehlungen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard.recommendations.map((rec, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        rec.type === 'action_required' 
                          ? 'bg-amber-500/10 border-amber-500/30' 
                          : 'bg-blue-500/10 border-blue-500/30'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{rec.icon}</span>
                        <div>
                          <p className="font-medium text-white">{rec.title}</p>
                          <p className="text-sm text-gray-400 mt-1">{rec.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Module Toggle */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Schnellzugriff Module</CardTitle>
              <CardDescription>Aktivieren oder deaktivieren Sie KI-Module mit einem Klick</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {modules.map((module) => (
                  <div 
                    key={module.id}
                    className={`p-4 rounded-lg border transition-all ${
                      module.enabled 
                        ? 'bg-purple-500/10 border-purple-500/30' 
                        : 'bg-slate-900/50 border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          module.enabled ? 'bg-purple-500/20' : 'bg-gray-500/20'
                        }`}>
                          <ModuleIcon moduleId={module.id} className={`w-5 h-5 ${
                            module.enabled ? 'text-purple-400' : 'text-gray-500'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-white">{module.name}</p>
                          <p className="text-xs text-gray-400">{module.features?.length || 0} Features</p>
                        </div>
                      </div>
                      <Switch
                        checked={module.enabled}
                        onCheckedChange={(checked) => updateModule(module.id, { enabled: checked })}
                        data-testid={`module-toggle-${module.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Actions */}
          {dashboard?.recent_actions && dashboard.recent_actions.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Letzte KI-Aktionen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.recent_actions.map((action, idx) => (
                    <div 
                      key={action.action_id || idx}
                      className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          action.status === 'executed' ? 'bg-green-400' :
                          action.status === 'pending' ? 'bg-amber-400' :
                          action.status === 'rejected' ? 'bg-red-400' :
                          'bg-gray-400'
                        }`} />
                        <div>
                          <p className="text-sm text-white">{action.title}</p>
                          <p className="text-xs text-gray-400">{action.module_id}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(action.created_at)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-6">
          {Object.entries(modulesByCategory).map(([catKey, category]) => (
            <Card key={catKey} className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <span className="text-xl">{category.icon}</span>
                  {category.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {category.modules?.map((module) => (
                    <div 
                      key={module.id}
                      className={`p-4 rounded-lg border ${
                        module.enabled 
                          ? 'bg-purple-500/10 border-purple-500/30' 
                          : 'bg-slate-900/50 border-slate-700'
                      }`}
                      data-testid={`module-card-${module.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                            module.enabled ? 'bg-purple-500/20' : 'bg-gray-500/20'
                          }`}>
                            {module.icon}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-white">{module.name}</h3>
                              {module.enabled ? (
                                <Badge className="bg-green-500/20 text-green-400">Aktiv</Badge>
                              ) : (
                                <Badge className="bg-gray-500/20 text-gray-400">Inaktiv</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-400 mb-3">{module.description}</p>
                            
                            {/* Features */}
                            <div className="flex flex-wrap gap-2 mb-3">
                              {module.features?.slice(0, 3).map((feature, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {feature}
                                </Badge>
                              ))}
                              {module.features?.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{module.features.length - 3} mehr
                                </Badge>
                              )}
                            </div>
                            
                            {/* Critical Permissions Warning */}
                            {module.enabled && module.requires_confirmation?.length > 0 && (
                              <div className="flex items-center gap-2 text-xs text-amber-400">
                                <Lock className="w-3 h-3" />
                                <span>Kritische Aktionen erfordern Bestätigung</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openModuleSettings(module)}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Einstellungen
                          </Button>
                          <Switch
                            checked={module.enabled}
                            onCheckedChange={(checked) => updateModule(module.id, { enabled: checked })}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Actions Tab */}
        <TabsContent value="actions" className="space-y-6">
          {/* Pending Confirmations */}
          {pendingActions.length > 0 && (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Ausstehende Bestätigungen ({pendingActions.length})
                </CardTitle>
                <CardDescription>
                  Diese KI-Aktionen erfordern Ihre Genehmigung
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pendingActions.map((action, idx) => (
                    <div 
                      key={action.action_id || idx}
                      className="p-4 bg-slate-900/50 rounded-lg border border-slate-700"
                      data-testid={`pending-action-${idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-white">{action.title}</p>
                          <p className="text-sm text-gray-400 mt-1">{action.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>{action.module_id}</span>
                            <span>{formatDate(action.created_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                            onClick={() => confirmAction(action.action_id, false)}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Ablehnen
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => confirmAction(action.action_id, true)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Genehmigen
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* All Actions */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Alle KI-Aktionen</CardTitle>
              <CardDescription>Chronologische Übersicht aller KI-Aktivitäten</CardDescription>
            </CardHeader>
            <CardContent>
              {allActions.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Noch keine KI-Aktionen</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {allActions.map((action, idx) => (
                    <div 
                      key={action.action_id || idx}
                      className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          action.status === 'executed' ? 'bg-green-500/20' :
                          action.status === 'approved' ? 'bg-green-500/20' :
                          action.status === 'pending' ? 'bg-amber-500/20' :
                          action.status === 'rejected' ? 'bg-red-500/20' :
                          'bg-gray-500/20'
                        }`}>
                          {action.status === 'executed' || action.status === 'approved' ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : action.status === 'pending' ? (
                            <Clock className="w-4 h-4 text-amber-400" />
                          ) : (
                            <X className="w-4 h-4 text-red-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm text-white">{action.title}</p>
                          <p className="text-xs text-gray-400">{action.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={
                          action.status === 'executed' ? 'bg-green-500' :
                          action.status === 'approved' ? 'bg-green-500' :
                          action.status === 'pending' ? 'bg-amber-500' :
                          'bg-red-500'
                        }>
                          {action.status === 'executed' ? 'Ausgeführt' :
                           action.status === 'approved' ? 'Genehmigt' :
                           action.status === 'pending' ? 'Ausstehend' :
                           'Abgelehnt'}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">{formatDate(action.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transparency Tab */}
        <TabsContent value="transparency" className="space-y-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-400" />
                KI-Transparenz
              </CardTitle>
              <CardDescription>
                Verstehen Sie, wie die KI Entscheidungen trifft
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Principle Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <Check className="w-8 h-8 text-green-400 mb-2" />
                  <h4 className="font-medium text-white mb-1">KI darf</h4>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• Analysieren</li>
                    <li>• Vorschlagen</li>
                    <li>• Warnen</li>
                    <li>• Empfehlungen aussprechen</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <X className="w-8 h-8 text-red-400 mb-2" />
                  <h4 className="font-medium text-white mb-1">KI darf nicht</h4>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• Preise eigenständig ändern</li>
                    <li>• Bestellungen auslösen</li>
                    <li>• Rabatte ohne Freigabe vergeben</li>
                    <li>• Buchungen abschließen</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <Shield className="w-8 h-8 text-purple-400 mb-2" />
                  <h4 className="font-medium text-white mb-1">Sicherheit</h4>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• Jede Aktion dokumentiert</li>
                    <li>• Jede Empfehlung begründet</li>
                    <li>• Nachvollziehbare Datennutzung</li>
                    <li>• Kein Autopilot ohne Freigabe</li>
                  </ul>
                </div>
              </div>
              
              {/* Data Usage */}
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  Datennutzung
                </h4>
                <p className="text-sm text-gray-400 mb-3">
                  Die KI nutzt ausschließlich Ihre eigenen Geschäftsdaten für Analysen und Empfehlungen:
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">Bestellhistorie</Badge>
                  <Badge variant="outline">Produktdaten</Badge>
                  <Badge variant="outline">Kundenprofile</Badge>
                  <Badge variant="outline">Umsatzdaten</Badge>
                  <Badge variant="outline">Zeitliche Muster</Badge>
                </div>
              </div>
              
              {/* Module Privacy */}
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-amber-400" />
                  Modul-Isolation
                </h4>
                <p className="text-sm text-gray-400">
                  Jedes KI-Modul kennt nur seinen eigenen Bereich. Die KI-Warenwirtschaft hat keinen 
                  Zugriff auf Marketing-Daten und umgekehrt. Keine KI greift ungefragt in andere Bereiche ein.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Module Settings Modal */}
      <Dialog open={showModuleModal} onOpenChange={setShowModuleModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedModule && (
                <>
                  <span className="text-2xl">{selectedModule.icon}</span>
                  {selectedModule.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedModule?.description}
            </DialogDescription>
          </DialogHeader>
          
          {selectedModule && (
            <div className="space-y-4 py-4">
              {/* Features */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Features</h4>
                <div className="space-y-1">
                  {selectedModule.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-white">
                      <Check className="w-4 h-4 text-green-400" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
              
              <Separator className="bg-slate-700" />
              
              {/* Permissions */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">Berechtigungen</h4>
                <div className="space-y-3">
                  {selectedModule.permissions && Object.entries(selectedModule.permissions).map(([key, value]) => {
                    const permDef = KI_MODULES[selectedModule.id]?.permissions?.find(p => p.key === key);
                    const isCritical = selectedModule.requires_confirmation?.includes(key);
                    
                    return (
                      <div key={key} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isCritical && <Lock className="w-4 h-4 text-amber-400" />}
                          <span className="text-sm text-white">{permDef?.name || key}</span>
                          {isCritical && (
                            <Badge className="text-xs bg-amber-500/20 text-amber-400">Kritisch</Badge>
                          )}
                        </div>
                        <Switch
                          checked={value}
                          onCheckedChange={(checked) => {
                            const newPerms = { ...modulePermissions, [key]: checked };
                            setModulePermissions(newPerms);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {selectedModule.requires_confirmation?.length > 0 && (
                <>
                  <Separator className="bg-slate-700" />
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-amber-400 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Kritische Aktionen erfordern immer Ihre Bestätigung
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModuleModal(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => {
                updateModule(selectedModule.id, { permissions: modulePermissions });
                setShowModuleModal(false);
              }}
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
